import React, { useEffect, useLayoutEffect } from '../lib/teact/teact';
import { withGlobal } from '../global';

import type { GlobalState } from '../global/types';
import type { ThemeKey } from '../types';
import type { UiLoaderPage } from './common/UiLoader';

import {
  DARK_THEME_BG_COLOR, LIGHT_THEME_BG_COLOR,
} from '../config';
import { selectSharedSettings } from '../global/selectors/sharedState';
import buildClassName from '../util/buildClassName';
import { ACCOUNT_SLOT, getAccountsInfo, getAccountSlotUrl } from '../util/multiaccount';
import { getInitialLocationHash } from '../util/routing';
import { hasStoredSession } from '../util/sessions';
import { updateSizes } from '../util/windowSize';

import useAppLayout from '../hooks/useAppLayout';
import usePreviousDeprecated from '../hooks/usePreviousDeprecated';

import Auth from './auth/Auth.async';
import UiLoader from './common/UiLoader';
import Main from './main/Main.async';
import Transition from './ui/Transition';

import styles from './App.module.scss';

type StateProps = {
  authState: GlobalState['authState'];
  authNeedsProfileSetup?: boolean;
  isTestServer?: boolean;
  theme: ThemeKey;
};

enum AppScreens {
  auth,
  main,
}

const TRANSITION_RENDER_COUNT = Object.keys(AppScreens).length / 2;
const App: React.FC<StateProps> = ({
  authState,
  authNeedsProfileSetup,
  isTestServer,
  theme,
}) => {
  const { isMobile } = useAppLayout();

  useEffect(() => {
    const hash = getInitialLocationHash();
    if (!hasStoredSession() && !ACCOUNT_SLOT && !hash) {
      const accounts = getAccountsInfo();
      Object.keys(accounts).forEach((key) => {
        const slot = Number(key);
        const account = accounts[slot];
        if (account) {
          const url = getAccountSlotUrl(slot);
          window.location.href = `${url}#${hash || 'login'}`;
        }
      });
    }
  }, []);

  useEffect(() => {
    const body = document.body;
    const handleDrag = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      if (!(e.target as HTMLElement).dataset.dropzone) {
        e.dataTransfer.dropEffect = 'none';
      } else {
        e.dataTransfer.dropEffect = 'copy';
      }
    };
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    body.addEventListener('drop', handleDrop);
    body.addEventListener('dragover', handleDrag);
    body.addEventListener('dragenter', handleDrag);

    return () => {
      body.removeEventListener('drop', handleDrop);
      body.removeEventListener('dragover', handleDrag);
      body.removeEventListener('dragenter', handleDrag);
    };
  }, []);

  let activeKey: number;
  let page: UiLoaderPage | undefined;

  if (authState) {
    switch (authState) {
      case 'authorizationStateWaitPhoneNumber':
        page = 'authPhoneNumber';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitCode':
      case 'authorizationStateWaitPassword':
      case 'authorizationStateWaitQrCode':
      case 'authorizationStateWaitRegistration':
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateClosed':
      case 'authorizationStateClosing':
      case 'authorizationStateLoggingOut':
        page = 'main';
        activeKey = AppScreens.main;
        break;
      case 'authorizationStateReady':
        page = authNeedsProfileSetup ? undefined : 'main';
        activeKey = authNeedsProfileSetup ? AppScreens.auth : AppScreens.main;
        break;
    }
  } else if (hasStoredSession()) {
    page = 'main';
    activeKey = AppScreens.main;
  } else {
    page = 'authPhoneNumber';
    activeKey = AppScreens.auth;
  }

  useEffect(() => {
    updateSizes();
  }, []);

  useEffect(() => {
    document.title = '';
  }, []);

  const prevActiveKey = usePreviousDeprecated(activeKey);

  // eslint-disable-next-line consistent-return
  function renderContent() {
    switch (activeKey) {
      case AppScreens.auth:
        return <Auth />;
      case AppScreens.main:
        return <Main isMobile={isMobile} />;
    }
  }

  useLayoutEffect(() => {
    document.body.classList.add(styles.bg);
  }, []);

  useLayoutEffect(() => {
    document.body.style.setProperty(
      '--theme-background-color',
      theme === 'dark' ? DARK_THEME_BG_COLOR : LIGHT_THEME_BG_COLOR,
    );
  }, [theme]);

  return (
    <UiLoader page={page} isMobile={isMobile}>
      <Transition
        name="fade"
        activeKey={activeKey}
        shouldCleanup
        className={buildClassName(
          'full-height',
          (activeKey === AppScreens.auth || prevActiveKey === AppScreens.auth) && 'is-auth',
        )}
        renderCount={TRANSITION_RENDER_COUNT}
      >
        {renderContent}
      </Transition>
      {activeKey === AppScreens.auth && isTestServer && <div className="test-server-badge">Test server</div>}
    </UiLoader>
  );
};

export default withGlobal(
  (global): StateProps => {
    return {
      authState: global.authState,
      authNeedsProfileSetup: global.authNeedsProfileSetup,
      theme: selectSharedSettings(global).theme,
      isTestServer: global.config?.isTestServer,
    };
  },
)(App);
